/**
 * Pure derivation for the store home "Reorder" card (flag `predictive_reorder`,
 * MOT-149): which suggestions to show, what each row says, and the summary
 * line. Copy is returned as locale keys plus parameters (`store.reorder.card.*`)
 * so the card translates them; tested like `deriveStoreAttention`.
 */
import { ReorderSuggestion, ReorderSuggestionsResult } from '../../components/reorder/reorderApi';

export type ReorderCardTone = 'red' | 'amber' | 'slate';

/** A locale key under `store.reorder.card` plus its interpolation values. */
export interface ReorderCardText {
  key: string;
  params?: Record<string, string | number>;
}

export interface ReorderCardRow {
  id: string;
  title: string;
  /** "12 days left · order 37" / "not counted · sells 0.5 a day", as a key and values. */
  meta: ReorderCardText;
  tone: ReorderCardTone;
}

export interface ReorderCardModel {
  rows: ReorderCardRow[];
  /** Count shown in the header pill: products to order now, else products to watch. */
  headlineCount: number;
  headlineTone: ReorderCardTone;
  /** The line under the header, as parts the card joins with " · ". */
  summary: ReorderCardText[];
  /** Products with a suggestion the card does not list. */
  moreCount: number;
}

export const REORDER_CARD_ROW_LIMIT = 5;

const formatDays = (days: number): string => (days < 10 ? days.toFixed(1).replace(/\.0$/, '') : Math.round(days).toLocaleString());

/** Units a day as a short rate: "{{n}} a day" above one a day, else "{{n}} a week". */
export function rateText(perDay: number): ReorderCardText {
  return perDay >= 1 ? { key: 'rateDay', params: { n: formatDays(perDay) } } : { key: 'rateWeek', params: { n: formatDays(perDay * 7) } };
}

export function describeReorderRow(s: ReorderSuggestion): ReorderCardRow {
  if (s.status === 'reorder_now') {
    const qty = (s.suggestedQty ?? 0).toLocaleString();
    const meta: ReorderCardText =
      s.daysOfSupply == null
        ? { key: 'orderNoneLeft', params: { qty } }
        : s.daysOfSupply <= 0
          ? { key: 'orderOutOfStock', params: { qty } }
          : { key: 'orderDaysLeft', params: { count: Math.round(s.daysOfSupply), qty } };
    return { id: s.productId, title: s.name, meta, tone: 'red' };
  }
  if (s.status === 'watch') {
    return {
      id: s.productId,
      title: s.name,
      meta: { key: 'watchDaysLeft', params: { count: Math.round(s.daysOfSupply ?? 0), point: s.reorderPoint.toLocaleString() } },
      tone: 'amber',
    };
  }
  if (s.status === 'no_count') {
    const rate = rateText(s.velocityPerDay);
    return {
      id: s.productId,
      title: s.name,
      meta: { key: s.countStale ? 'staleCount' : 'notCounted', params: { rateKey: rate.key, n: rate.params?.n ?? '', point: s.reorderPoint.toLocaleString() } },
      tone: 'slate',
    };
  }
  return {
    id: s.productId,
    title: s.name,
    meta:
      s.daysOfSupply == null
        ? { key: 'okNoSales', params: { onHand: s.onHand?.toLocaleString() ?? '-' } }
        : { key: 'okDaysLeft', params: { count: Math.round(s.daysOfSupply), onHand: s.onHand?.toLocaleString() ?? '-' } },
    tone: 'slate',
  };
}

/**
 * Order-now rows first, then watch, then uncounted sellers, at most
 * `REORDER_CARD_ROW_LIMIT`; products that are fine are never listed. The
 * server already ranks, so the order is kept. `moreCount` counts the products
 * in the whole ranked list (`counts`) that the card does not show, not only
 * the page it received.
 */
export function deriveReorderCard(result: ReorderSuggestionsResult | null | undefined): ReorderCardModel {
  if (!result) return { rows: [], headlineCount: 0, headlineTone: 'slate', summary: [], moreCount: 0 };
  const listed = result.suggestions.filter((s) => s.status !== 'ok');
  const rows = listed.slice(0, REORDER_CARD_ROW_LIMIT).map(describeReorderRow);
  const { reorderNow, watch, noCount } = result.counts;
  const headlineCount = reorderNow > 0 ? reorderNow : watch;
  const headlineTone: ReorderCardTone = reorderNow > 0 ? 'red' : watch > 0 ? 'amber' : 'slate';
  const summary: ReorderCardText[] = [];
  if (reorderNow > 0) summary.push({ key: 'summaryOrderNow', params: { count: reorderNow } });
  if (watch > 0) summary.push({ key: 'summaryWatch', params: { count: watch } });
  if (noCount > 0) summary.push({ key: 'summaryNoCount', params: { count: noCount } });
  if (summary.length === 0) {
    if (result.selectionSize === 0) summary.push({ key: 'summaryNoSelection' });
    else if (result.total === 0) summary.push({ key: 'summaryNoSales', params: { days: result.window.days } });
    else summary.push({ key: 'summaryAllFine' });
  }
  return { rows, headlineCount, headlineTone, summary, moreCount: Math.max(0, reorderNow + watch + noCount - rows.length) };
}
